import { CloseCircleOutlined, EyeOutlined, MergeCellsOutlined } from '@ant-design/icons';
import Column from 'antd/es/table/Column';
import { useTranslation } from 'react-i18next';
import { shortDate } from '@app/utils';
import { usePermissions } from '@data/hooks';
import { QuestionView } from '@data/sdk/match';
import { Namespace } from '@data/utils/enums';
import { AntdGrid, AntdTable, Button } from '@ui/components';
import { useSuggestedQuestions } from './hooks';

const { useBreakpoint } = AntdGrid;

type SuggestedQuestionsTableProps = {
  threshold: number;
};

export const SuggestedQuestionsTable = ({ threshold }: SuggestedQuestionsTableProps) => {
  const { t } = useTranslation([Namespace.ADMIN]);
  const { md } = useBreakpoint();
  const { IS_REVIEWER } = usePermissions();

  const {
    current,
    pageSize,
    suggestedQuestions,
    isSuggestedQuestionsLoading,
    handleTableChange,
    handleMergeIgnore,
    handleQuestionsMerge,
    navigateToQuestionMerge,
  } = useSuggestedQuestions(threshold);

  return (
    <AntdTable
      dataSource={suggestedQuestions?.items}
      pagination={
        suggestedQuestions?.items.length
          ? {
              current,
              pageSize,
              total: suggestedQuestions?.count,
              size: 'middle',
              placement: ['bottomStart'],
            }
          : false
      }
      size="middle"
      onChange={handleTableChange}
      loading={isSuggestedQuestionsLoading}
      scroll={{ x: md ? undefined : 'max-content' }}
      className="cursor-pointer mt-4"
    >
      <Column title={t('question_page.merge.table.question_text')} dataIndex="text" key="text" />
      <Column
        title={t('question_page.merge.table.skipped')}
        className="text-right"
        dataIndex="skippedCount"
        key="skippedCount"
        width={140}
      />
      <Column
        title={t('question_page.merge.table.answered')}
        dataIndex="answeredCount"
        className="text-right"
        key="answeredCount"
        width={140}
      />
      <Column
        width={140}
        title={t('question_page.merge.table.linked_trials')}
        className="text-right"
        dataIndex="trialCount"
        key="trialCount"
      />
      <Column
        title={t('question_page.merge.table.created')}
        width={160}
        dataIndex="createdAt"
        key="createdAt"
        render={(date) => shortDate(date)}
      />
      {!IS_REVIEWER && (
        <Column
          width={350}
          render={(_, question: QuestionView) => (
            <div className="flex items-center">
              <Button
                type="text"
                icon={<EyeOutlined />}
                className="text-primary-600"
                onClick={() => navigateToQuestionMerge(question.id)}
              >
                {t('question_page.merge.table.go_to_question')}
              </Button>
              <Button
                type="text"
                icon={<MergeCellsOutlined />}
                className="text-primary-600"
                onClick={() => handleQuestionsMerge(question.id)}
              >
                {t('question_page.merge.table.merge')}
              </Button>
              <Button
                type="text"
                icon={<CloseCircleOutlined />}
                className="text-error"
                onClick={() => handleMergeIgnore(question.id)}
              >
                {t('question_page.merge.table.ignore')}
              </Button>
            </div>
          )}
        />
      )}
    </AntdTable>
  );
};
